import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaUsers, FaTasks, FaClock, FaMoneyBillWave, FaCalendarAlt, FaBell,
  FaSearch, FaPlus, FaChartLine, FaUserCheck, FaDownload, FaFire, FaUmbrellaBeach, FaSignOutAlt, FaComments,
  FaLaptopCode, FaHandshake, FaExclamationTriangle
} from "react-icons/fa";
import { collection, onSnapshot, getDocs, setDoc, doc } from "firebase/firestore";
import { db } from "../../firebase";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { Link, useNavigate } from "react-router-dom";
import HrChatbox from "./HrChatbox";
import AddAppModal from "./tech/AddAppModal";

const StatCard = ({ icon: Icon, title, value, color }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    className="relative group cursor-pointer"
  >
    <div className="backdrop-blur-3xl bg-white/10 border border-white/20 rounded-3xl p-10 text-center">
      <div className={`text-6xl font-black bg-gradient-to-r ${color} bg-clip-text text-transparent`}>
        {value}
      </div>
      <p className="text-white/70 text-sm mt-3">{title}</p>
      <Icon className="absolute top-4 right-4 text-white/30 text-4xl" />
    </div>
  </motion.div>
);

const QuickAction = ({ icon: Icon, label, to, color }) => (
  <Link to={to}>
    <motion.button
      whileHover={{ scale: 1.15, rotate: 5 }}
      whileTap={{ scale: 0.95 }}
      className={`bg-gradient-to-br ${color || 'from-cyan-500 to-blue-500'} text-white w-28 h-28 rounded-3xl shadow-2xl flex flex-col items-center justify-center gap-3 hover:shadow-cyan-500/50 transition-all`}
    >
      <Icon className="text-3xl" />
      <span className="text-xs font-bold text-center px-1 leading-tight">{label}</span>
    </motion.button>
  </Link>
);

export default function HrDashboard() {
  const { user, logout } = useEmployeeAuth();
  const [employees, setEmployees] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [apps, setApps] = useState([
    { id: 'customer-app', title: 'Customer App', owner: 'Driveeasy-Wheelpro', repo: 'driveeasy-app-frontend', branch: 'abhishek-dev', screensPath: 'src/screens' },
    { id: 'partner-app', title: 'Partner App', owner: 'Driveeasy-Wheelpro', repo: 'driveeasy-partnerapp-frontend', branch: 'abhishek-dev', screensPath: 'src/screens' }
  ]);
  const [blockerCounts, setBlockerCounts] = useState({ 'customer-app': 0, 'partner-app': 0 });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const navigate = useNavigate();


  useEffect(() => {
    const unsubs = [
      onSnapshot(collection(db, "employees"), s => setEmployees(s.docs.map(d => ({ id: d.id, ...d.data() })))),
      onSnapshot(collection(db, "leaves"), s => setLeaves(s.docs.map(d => ({ id: d.id, ...d.data() })))),
      onSnapshot(collection(db, "tasks"), s => setTasks(s.docs.map(d => ({ id: d.id, ...d.data() })))),
      onSnapshot(collection(db, "timesheets"), s => setTimesheets(s.docs.map(d => ({ id: d.id, ...d.data() })))),
      onSnapshot(collection(db, "payouts"), s => setPayouts(s.docs.map(d => ({ id: d.id, ...d.data() })))),
      onSnapshot(collection(db, "tech_apps"), async (s) => {
        const fetchedApps = s.docs.map(d => ({ id: d.id, ...d.data() }));
        // Migration: Seed if empty
        if (fetchedApps.length === 0) {
          const initialApps = [
            { id: 'customer-app', title: 'Customer App', owner: 'Driveeasy-Wheelpro', repo: 'driveeasy-app-frontend', branch: 'abhishek-dev', screensPath: 'src/screens' },
            { id: 'partner-app', title: 'Partner App', owner: 'Driveeasy-Wheelpro', repo: 'driveeasy-partnerapp-frontend', branch: 'abhishek-dev', screensPath: 'src/screens' }
          ];
          for (const app of initialApps) {
            await setDoc(doc(db, 'tech_apps', app.id), app);
          }
        } else {
          setApps(fetchedApps);
        }
      })
    ];
    return () => unsubs.forEach(u => u());
  }, []);

  // Effect to listen to blockers for all apps
  useEffect(() => {
    if (apps.length === 0) return;

    const blockerUnsubs = apps.map(app => {
      const collName = `tech_${app.id.replace(/-/g, '_')}_screens`;
      return onSnapshot(collection(db, collName), (s) => {
        const count = s.docs.filter(d => d.data().blocker?.trim().length > 0).length;
        setBlockerCounts(prev => ({ ...prev, [app.id]: count }));
      });
    });

    return () => blockerUnsubs.forEach(u => u());
  }, [apps]);

  const stats = useMemo(() => {
    const total = employees.length;
    const active = employees.filter(e => e.status === "active").length;
    const pendingLeaves = leaves.filter(l => l.status === "pending").length;
    const pendingTasks = tasks.filter(t => t.status === "pending").length;

    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();

    const monthPay = payouts
      .filter(p => p.paidOn?.seconds && new Date(p.paidOn.seconds * 1000).getMonth() === month)
      .reduce((s, p) => s + (p.finalAmount || 0), 0);

    const monthHours = timesheets
      .filter(t => t.createdAt?.seconds && new Date(t.createdAt.seconds * 1000).getMonth() === month)
      .reduce((s, t) => s + (t.totalHours || 0), 0);

    // Tech Blockers
    const totalBlockers = Object.values(blockerCounts).reduce((a, b) => a + b, 0);

    return { total, active, pendingLeaves, pendingTasks, monthPay, monthHours, totalBlockers };
  }, [employees, leaves, tasks, timesheets, payouts, blockerCounts]);

  const recentActivity = useMemo(() => {
    const items = [
      ...leaves.filter(l => l.status === "approved").map(l => ({ type: "leave", ...l })),
      ...tasks.filter(t => t.status === "completed").map(t => ({ type: "task", ...t })),
      ...timesheets.slice(0, 5).map(t => ({ type: "clock", ...t })),
    ];
    return items
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
      .slice(0, 6);
  }, [leaves, tasks, timesheets]);

  const topPerformers = useMemo(() => {
    const map = {};
    timesheets.forEach(t => {
      const email = t.employeeEmail;
      if (!map[email]) map[email] = { email, hours: 0, count: 0 };
      map[email].hours += Number(t.totalHours || 0);
      map[email].count++;
    });
    return Object.values(map)
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 3)
      .map(p => {
        const emp = employees.find(e => e.email === p.email);
        return { ...p, name: emp?.name || p.email.split("@")[0] };
      });
  }, [timesheets, employees]);

  const quickLinks = [
    { icon: FaPlus, label: "Add Employee", to: "/employee/hr-manage-employees" },
    { icon: FaTasks, label: "Assign Task", to: "/employee/hr-assign-tasks" },
    { icon: FaMoneyBillWave, label: "Run Payroll", to: "/employee/hr-payouts" },
    { icon: FaCalendarAlt, label: "Holidays", to: "/employee/calendar-page" },
    { icon: FaUmbrellaBeach, label: "Leaves", to: "/employee/hr-leaves" },
    // DYNAMIC TECH APPS
    ...apps.map(app => ({
      icon: app.id === 'customer-app' ? FaLaptopCode : FaHandshake,
      label: app.title,
      to: `/employee/tech/${app.id}`,
      color: app.id === 'customer-app' ? "from-orange-500 to-red-500" : "from-blue-600 to-indigo-600"
    }))
  ];

  const handleLogout = () => {
    logout();
    navigate("/employee/login");
  };

  return (
    <div className="min-h-screen overflow-hidden bg-gradient-to-br from-neutral-950 via-purple-950 to-indigo-950">

      {/* TOP BAR WITH LOGOUT */}
      <div className="fixed top-0 left-0 right-0 z-50 backdrop-blur-3xl bg-black/30 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="text-2xl font-black text-white">DriveEasy HR</div>
            <div className="text-cyan-300 text-sm">Welcome, {user?.name || "HR"}</div>
          </div>

          {/* GLOWING LOGOUT BUTTON */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleLogout}
            className="px-6 py-3 bg-gradient-to-r from-red-500 to-rose-600 rounded-2xl text-white font-bold shadow-xl hover:shadow-red-500/50 flex items-center gap-3"
          >
            <FaSignOutAlt /> Logout
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.1 }}
            onClick={() => setChatOpen(true)}
            className="relative p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl shadow-xl"
          >
            <FaComments className="text-white text-xl" />
            {false && <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full" />}
          </motion.button>

        </div>
      </div>

      {/* HERO */}
      <div className="pt-32 pb-6 px-8 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-7xl font-black bg-gradient-to-r from-white via-white/90 to-white/70 bg-clip-text text-transparent"
        >
          HR Command Center
        </motion.h1>
        <p className="text-white/60 text-xl mt-4">Real-time pulse of your workforce</p>
      </div>

      {/* STATS GRID */}
      <div className="max-w-7xl mx-auto px-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-6">
        <StatCard icon={FaUsers} title="Total Team" value={stats.total} color="from-emerald-400 to-teal-400" />
        <StatCard icon={FaUserCheck} title="Active Today" value={stats.active} color="from-cyan-400 to-blue-400" />
        <StatCard icon={FaBell} title="Leaves" value={stats.pendingLeaves} color="from-orange-400 to-red-400" />
        <StatCard icon={FaTasks} title="Open Tasks" value={stats.pendingTasks} color="from-purple-400 to-pink-400" />
        <StatCard icon={FaMoneyBillWave} title="Payroll" value={`₹${(stats.monthPay / 100000).toFixed(1)}L`} color="from-green-400 to-emerald-400" />
      </div>

      {/* MAIN GRID */}
      <div className="max-w-7xl mx-auto px-8 grid lg:grid-cols-2 gap-8">

        {/* LEFT: QUICK ACTIONS */}
        <motion.div
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          className="space-y-3"
        >
          <div className="backdrop-blur-3xl bg-white/10 border border-white/20 rounded-3xl p-8">
            <h3 className="text-2xl font-bold text-white mb-6">Lightning Actions</h3>
            <div className="grid grid-cols-4 gap-6">
              {quickLinks.map((link, i) => (
                <QuickAction key={i} {...link} />
              ))}
              {/* Add New Tech Tracker button */}
              <motion.button
                whileHover={{ scale: 1.15, rotate: -5 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsAddModalOpen(true)}
                className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white w-28 h-28 rounded-3xl shadow-2xl flex flex-col items-center justify-center gap-3 hover:shadow-indigo-500/50 transition-all border border-white/10"
              >
                <FaPlus className="text-3xl" />
                <span className="text-xs font-bold text-center">New App<br />Tracker</span>
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Modal for adding new app */}
        <AddAppModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAppCreated={(newApp) => {
            // New app is added to Firestore, onSnapshot will pick it up
            console.log("New App Created:", newApp);
          }}
        />


        {/* RIGHT: ALERTS & EXPORT */}
        <motion.div
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          className="space-y-3"
        >
          <div className="backdrop-blur-3xl bg-white/10 border border-white/20 rounded-3xl p-8">
            <h3 className="text-2xl font-bold text-white mb-6">Pending Alerts</h3>
            {stats.pendingLeaves > 0 && (
              <div className="p-4 bg-orange-500/20 border border-orange-400/50 rounded-2xl mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-orange-300">{stats.pendingLeaves} leave requests</span>
                  <Link to="/employee/hr-leaves" className="text-cyan-300 font-bold text-sm">Review</Link>
                </div>
              </div>
            )}
            {stats.pendingTasks > 0 && (
              <div className="p-4 bg-red-500/20 border border-red-400/50 rounded-2xl">
                <div className="flex justify-between items-center">
                  <span className="text-red-300">{stats.pendingTasks} tasks overdue</span>
                  <Link to="/employee/hr-assign-tasks" className="text-cyan-300 font-bold text-sm">Fix</Link>
                </div>
              </div>
            )}

            {/* Tech Blocker Alert */}
            {stats.totalBlockers > 0 && (
              <div className="p-4 bg-rose-600/20 border border-rose-500/50 rounded-2xl animate-pulse">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <FaExclamationTriangle className="text-rose-400" />
                    <span className="text-rose-300 font-bold">{stats.totalBlockers} Tech Blockers</span>
                  </div>
                  <Link to="/employee/tech/customer-app" className="text-cyan-300 font-bold text-sm">View</Link>
                </div>
              </div>
            )}

            {stats.pendingLeaves === 0 && stats.pendingTasks === 0 && stats.totalBlockers === 0 && (
              <p className="text-center text-white/60 py-8">All systems green</p>
            )}
          </div>


        </motion.div>
      </div>

      {/* HR CHATBOX */}
      <AnimatePresence>
        {chatOpen && <HrChatbox isOpen={chatOpen} onClose={() => setChatOpen(false)} />}
      </AnimatePresence>

      {/* FOOTER */}
      <div className="text-center mt-6 text-white/40 text-sm mb-6">
        <p>
          Powered by <strong className="text-cyan-300">DriveEasy HR</strong> •
          Real-time • Secure • Built for scale
        </p>
      </div>
    </div>
  );
}
